import {
  asapScheduler,
  defer,
  finalize,
  from,
  map,
  type Observable,
  observeOn,
  of,
  race,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';

/**
 * Subscribes `recorder$` before `toWait$` is subscribed, and keeps it
 * subscribed until the wait ends. The wait reads the latest recorded value.
 */
export const whileRecording = <R, T>(
  recorder$: Observable<R>,
  toWait$: (recorded$: Observable<R>) => Observable<T>
): Observable<T> =>
  defer(() => {
    const recorded$ = new ReplaySubject<R>(1);
    const recording = recorder$.subscribe(recorded$);
    return toWait$(recorded$).pipe(finalize(() => recording.unsubscribe()));
  });

/** Runs `run` on subscribe, and aborts its signal on unsubscribe. */
export const fromAbortable = <T>(
  run: (signal: AbortSignal) => Promise<T>
): Observable<T> =>
  defer(() => {
    const controller = new AbortController();
    return from(run(controller.signal)).pipe(
      finalize(() => controller.abort())
    );
  });

export type DeckExportRun<Rows> = {
  rows$: Observable<Rows>;
  cancel$: Observable<unknown>;
  saveDeck$: (rows: Rows) => Observable<unknown>;
  teardown: VoidFunction;
};

/**
 * Captures the rows and saves the deck. A cancel while capturing ends the run
 * as aborted; once the deck is being saved, cancel is ignored. `teardown` runs
 * however the run ends. A cancel is handled after the action that requested it.
 */
export const runDeckExport$ = <Rows>({
  rows$,
  cancel$,
  saveDeck$,
  teardown,
}: DeckExportRun<Rows>): Observable<'aborted' | 'saved'> =>
  race(
    rows$.pipe(map(rows => ({ rows }))),
    cancel$.pipe(
      take(1),
      observeOn(asapScheduler),
      map((): 'aborted' => 'aborted')
    )
  ).pipe(
    take(1),
    switchMap(captured =>
      captured === 'aborted'
        ? of(captured)
        : saveDeck$(captured.rows).pipe(
            take(1),
            map((): 'saved' => 'saved')
          )
    ),
    finalize(teardown)
  );
